import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { GroceryListDetail } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { applyGroceryEvent } from './display';
import { bindGroceryEvents } from './groceryEventStream';
import { groceryKeys } from './queries';

const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 15_000;

export interface UseGroceryEventsOptions {
  /** Another member deleted the list while it was open. */
  onListDeleted?: () => void;
}

/**
 * Live sync (D14): subscribes to the list's SSE stream while `listId` is set
 * and applies every event straight onto the cached `groceryKeys.detail(listId)`
 * query, so every open shopper sees checks/edits from the others without a
 * refetch. Reconnects on error with capped exponential backoff, and refetches
 * the list once a reconnect succeeds (events sent while disconnected are not
 * replayed). Tears down on unmount or when `listId`/`enabled` changes.
 */
export function useGroceryEvents(
  listId: string,
  enabled: boolean,
  options: UseGroceryEventsOptions = {},
) {
  const qc = useQueryClient();
  const onListDeletedRef = useRef(options.onListDeleted);
  onListDeletedRef.current = options.onListDeleted;

  useEffect(() => {
    if (!enabled || listId === '') return undefined;

    let source: EventSource | null = null;
    let unbind: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = BASE_RETRY_MS;
    let disconnected = false;
    let stopped = false;

    function teardown() {
      unbind?.();
      unbind = null;
      source?.close();
      source = null;
    }

    function connect() {
      if (stopped) return;
      source = new EventSource(api.groceries.eventsUrl(listId), { withCredentials: true });
      source.onopen = () => {
        retryDelay = BASE_RETRY_MS;
        if (disconnected) {
          disconnected = false;
          void qc.invalidateQueries({ queryKey: groceryKeys.detail(listId) });
        }
      };
      unbind = bindGroceryEvents(source, (event) => {
        if (event.type === 'list.deleted') {
          onListDeletedRef.current?.();
          return;
        }
        qc.setQueryData<GroceryListDetail>(groceryKeys.detail(listId), (previous) =>
          previous ? applyGroceryEvent(previous, event) : previous,
        );
      });
      source.onerror = () => {
        teardown();
        disconnected = true;
        if (stopped) return;
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
      };
    }

    connect();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      teardown();
    };
  }, [listId, enabled, qc]);
}
